import { Navigate, useLocation, useParams } from 'react-router';

/**
 * `/atlas-whitelist/*` and `/agents/*` → `/cluster-access/*`, WITH the query string.
 *
 * These six paths are in Slack threads, runbooks, the Activity trail and the docs,
 * and the links that matter carry a query string: `?cluster=`, `?status=__gone__`,
 * `?tab=pending`, `?tab=suppressed`. A bare `<Navigate to="/x" />` drops it — which
 * would leave every one of those links landing on an unfiltered screen and looking,
 * to the person who pasted it, like the data went away.
 *
 * That is the single most likely bug in this merge, so it is one component with one
 * job instead of six inline redirects, and each of the thirteen URLs is walked in a
 * browser rather than reasoned about.
 *
 * `promote` handles the one legacy URL that put its subject in the query string
 * rather than the path (`/atlas-whitelist?project=<key>`). It moves into the path
 * and is removed from the search, exactly as `AtlasWhitelistPage` did inline before
 * this file existed.
 */
export function LegacyRedirect({
  to,
  promote,
}: {
  /** Target path; `:name` segments are filled from the matched route's params. */
  to: string;
  /** Query key whose value becomes the path instead. */
  promote?: { key: string; to: string };
}) {
  const params = useParams();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  let template = to;
  const promoted = promote ? query.get(promote.key) : null;
  if (promote && promoted) {
    template = promote.to;
    query.delete(promote.key);
  }

  const pathname = template
    .split('/')
    .map((seg) => {
      if (!seg.startsWith(':')) return seg;
      const name = seg.slice(1);
      const value = name === 'promoted' ? promoted : params[name];
      return encodeURIComponent(value ?? '');
    })
    .join('/');

  const rest = query.toString();
  return <Navigate replace to={{ pathname, search: rest ? `?${rest}` : '' }} />;
}
